import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";

export interface DaycareCapacity {
  id: string | null;
  totalCapacity: number | null;
  limitBySize: boolean;
  capacitySmall: number | null;
  capacityMedium: number | null;
  capacityLarge: number | null;
}

export interface DaycareCapacityInput {
  totalCapacity?: number | null;
  limitBySize?: boolean;
  capacitySmall?: number | null;
  capacityMedium?: number | null;
  capacityLarge?: number | null;
}

const EMPTY: DaycareCapacity = {
  id: null,
  totalCapacity: null,
  limitBySize: false,
  capacitySmall: null,
  capacityMedium: null,
  capacityLarge: null,
};

export const daycareCapacityKey = (clinicId: string | null) => ["daycare-capacity", clinicId];

export function useDaycareCapacity() {
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useQuery({
    queryKey: daycareCapacityKey(clinicId),
    queryFn: async (): Promise<DaycareCapacity> => {
      if (!clinicId) return EMPTY;
      const { data, error } = await supabase
        .from("daycare_capacity")
        .select("id, total_capacity, limit_by_size, capacity_small, capacity_medium, capacity_large")
        .eq("clinic_id", clinicId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return EMPTY;
      return {
        id: data.id,
        totalCapacity: data.total_capacity,
        limitBySize: data.limit_by_size,
        capacitySmall: data.capacity_small,
        capacityMedium: data.capacity_medium,
        capacityLarge: data.capacity_large,
      };
    },
    enabled: !!clinicId,
  });
}

/** Single row per clinic (clinic_id is UNIQUE), so upsert on conflict keeps this idempotent. */
export function useSaveDaycareCapacity() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useMutation({
    mutationFn: async (input: DaycareCapacityInput) => {
      if (!clinicId) throw new Error("no_clinic_selected");
      const { error } = await supabase.from("daycare_capacity").upsert(
        {
          clinic_id: clinicId,
          ...(input.totalCapacity !== undefined ? { total_capacity: input.totalCapacity } : {}),
          ...(input.limitBySize !== undefined ? { limit_by_size: input.limitBySize } : {}),
          ...(input.capacitySmall !== undefined ? { capacity_small: input.capacitySmall } : {}),
          ...(input.capacityMedium !== undefined ? { capacity_medium: input.capacityMedium } : {}),
          ...(input.capacityLarge !== undefined ? { capacity_large: input.capacityLarge } : {}),
        },
        { onConflict: "clinic_id" },
      );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: daycareCapacityKey(clinicId) });
    },
  });
}

/** Pets occupying a daycare slot today — drives the "x/y" usage read-out. */
export function useDaycareOccupancyToday() {
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useQuery({
    queryKey: ["daycare-occupancy-today", clinicId],
    queryFn: async (): Promise<number> => {
      if (!clinicId) return 0;
      const today = new Date().toLocaleDateString("en-CA");
      const { count, error } = await (supabase as any) // eslint-disable-line @typescript-eslint/no-explicit-any
        .from("stay_reservation_days")
        .select("id, stay_reservations!inner(module_type)", { count: "exact", head: true })
        .eq("clinic_id", clinicId)
        .eq("stay_date", today)
        .in("status", ["scheduled", "checked_in"])
        .in("stay_reservations.module_type", ["daycare", "both"]);
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!clinicId,
  });
}
